import { categoryInventorySelection } from "./category-localization.mjs";
import { publishedLandingStockIdentity } from "./published-landing-bindings.mjs";
import { landingFallbackMatchesSource } from "./landing-path-candidates.mjs";

export const STOCK_MARKETS = Object.freeze({
  uk: "GB", ie: "IE", us: "US", ca: "CA", au: "AU", nz: "NZ", za: "ZA",
  de: "DE", fr: "FR", es: "ES", it: "IT", pt: "PT", dk: "DK", se: "SE",
  mt: "MT", br: "BR", co: "CO", ec: "EC", mx: "MX", be: "BE", pl: "PL",
  sk: "SK", no: "NO", bg: "BG", fi: "FI", nl: "NL", at: "AT", cz: "CZ", in: "IN",
});
const normalize = value => `/${String(value || "").replace(/^\/+|\/+$/g, "")}`;

// Classification comes from the resolved page, never stock availability.
export function isInformationalLanding(page) {
  const sourceFamily = String(page?.page_type || "").trim().toLowerCase();
  if (page?.exactLegacySource && ["onboarding", "owner acquisition (list your car)"].includes(sourceFamily)) return true;
  if (page?.exactLegacySource && sourceFamily === "marketing" &&
      !["rental_type", "era", "make", "model", "vehicle_type", "city", "region"].some(key =>
        page.filter_params?.[key] !== undefined && page.filter_params[key] !== null &&
        page.filter_params[key] !== "")) return true;
  const reviews = page?.page_type === "marketing" && page?.category === "reviews" &&
    String(page.full_path || "").split("/").at(-1) === "reviews";
  return (reviews || ["info", "static", "reviews", "article", "legal", "help / info"].includes(page?.page_type)) &&
    !Object.keys(page.filter_params || {}).some(key =>
      ["rental_type", "era", "make", "model", "vehicle_type"].includes(key));
}

// An explicit country wrapper may resolve to an authored shared page. The
// resolved page, matched path and DTO must agree; this is not prefix stripping.
export function resolvedWrappedStockIdentity(data, pathname, brand) {
  if (!["bookaclassic", "driveshare"].includes(brand)) throw Error("Unknown stock brand authority");
  if (isInformationalLanding(data?.page)) return null;
  const published = publishedLandingStockIdentity(data, pathname, brand);
  if (published) return published;
  const publicPath = normalize(pathname);
  const parts = publicPath.slice(1).split("/");
  const countryCode = STOCK_MARKETS[parts[0]];
  const storedPath = normalize(data?.page?.full_path);
  if (!countryCode) return null;
  if (storedPath === "/" && publicPath === `/${parts[0]}`) return null; // Existing country-root owner, outside rental wrapping.
  if (storedPath === "/" || !data?.page) throw Error("Unresolved rental page authority");
  const category = categoryInventorySelection(data.page);
  const path = category?.path || storedPath;
  let locale = category?.locale || String(data.page.language || "").toLowerCase().split("-")[0];
  if (!locale && publicPath === storedPath) return null;
  if (!locale) throw Error("Resolved stock language is missing");
  const matchingPublicPaths = [storedPath, `/${parts[0]}${storedPath}`, `/${parts[0]}/${locale}${storedPath}`];
  // Existing Finnish recovery path resolves to this exact native authored
  // source. This is lookup/stock authority, not a new canonical or redirect.
  // All DTO, country and language checks below still apply.
  if (countryCode === "FI" && locale === "fi" &&
      storedPath === "/fi/sotia-edeltavan-vanhan-ajan-auton-vuokraus-haihin" &&
      publicPath === "/fi/pre-war-and-vintage-car-rental") {
    matchingPublicPaths.push(publicPath);
  }
  // Native/default-language aliases have an existing, explicit stored key.
  const storedParts = storedPath.slice(1).split("/");
  if (landingFallbackMatchesSource(data, publicPath)) {
    matchingPublicPaths.push(publicPath);
  }
  // A genuinely published translation may retain a country-qualified native
  // storage key. Its public URL adds the source language exactly once. Keep
  // the original stock key and all country/language/brand DTO checks below.
  if (data.page.active === true && data.page.locale === parts[0] &&
      data.page.language === parts[1] && storedParts[0] === parts[0] &&
      publicPath === `/${parts[0]}/${parts[1]}/${storedParts.slice(1).join("/")}`) {
    matchingPublicPaths.push(publicPath);
  }
  if (storedParts[0] === parts[0] && (storedParts[1] === locale ||
      (countryCode === "NO" && ["no", "nb"].includes(storedParts[1]) && ["no", "nb"].includes(locale)))) {
    matchingPublicPaths.push(`/${parts[0]}/${storedParts.slice(2).join("/")}`);
  }
  const alias = data.routePolicy;
  const explicitAlias = alias?.kind === "alias_200" &&
    normalize(alias.alias) === publicPath && normalize(alias.canonical) === path;
  if (!matchingPublicPaths.includes(publicPath) && !explicitAlias) {
    throw Error("Public route does not belong to the resolved stock page");
  }
  if (data.matchedPath && normalize(data.matchedPath) !== storedPath) {
    throw Error("Matched path disagrees with resolved page");
  }
  const pageCountry = STOCK_MARKETS[String(data.page.locale || "").toLowerCase()];
  const filterCountry = data.page.filter_params?.country_code;
  if ((pageCountry && pageCountry !== countryCode) ||
      (filterCountry && filterCountry !== countryCode)) throw Error("Resolved page crosses country ownership");
  const inventory = data.fixedInventory;
  if (inventory?.state === "ready" && countryCode === "NO" &&
      ["no", "nb"].includes(locale) && ["no", "nb"].includes(inventory.locale)) {
    // Preserve the API's existing Norwegian alias while recording the actual
    // stored locale; never derive stock locale from a visitor preference.
    locale = inventory.locale;
  }
  if (inventory?.state === "ready" &&
      (publicPath !== storedPath || inventory.countryCode || inventory.locale || inventory.brand) &&
      (normalize(inventory.path) !== normalize(path) || inventory.countryCode !== countryCode ||
       inventory.locale !== locale || inventory.brand !== brand)) {
    throw Object.assign(Error("Resolved stock DTO identity mismatch"), { status: 503 });
  }
  return { path, countryCode, locale, brand };
}

export function stockMatchesIdentity(stock, identity) {
  const sameLocale = stock?.locale === identity.locale ||
    (identity.countryCode === "NO" && ["no", "nb"].includes(identity.locale) &&
      ["no", "nb"].includes(stock?.locale));
  return stock?.state === "ready" && stock.path === identity.path &&
    stock.countryCode === identity.countryCode && sameLocale &&
    stock.brand === identity.brand && typeof stock.generationId === "string" &&
    stock.generationId.length > 0 && Array.isArray(stock.cohorts) && stock.cohorts.length > 0 &&
    // Twelve is the selection target, not proof of source completeness.
    // Published regional/sparse cohorts may contain fewer real local cars.
    stock.cohorts.every(cohort => Array.isArray(cohort.cars) &&
      new Set(cohort.cars.map(car => car.id)).size === cohort.cars.length &&
      cohort.cars.every(car => car.countryCode === identity.countryCode) &&
      cohort.orderedCarIds?.length === cohort.cars.length &&
      cohort.orderedCarIds.every((id, index) => id === cohort.cars[index].id));
}
